import axios from 'axios';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let refreshSubscribers = [];

// Function to subscribe to token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to notify all subscribers when token is refreshed
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Request interceptor to add token to headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and expiration warnings
apiClient.interceptors.response.use(
  (response) => {
    // Check for token expiration warnings in response headers
    const expiresIn = response.headers['x-token-expires-in'];
    const expiringSoon = response.headers['x-token-expiring-soon'];

    if (expiringSoon === 'true' && expiresIn) {
      // Dispatch custom event for token expiration warning
      const event = new CustomEvent('tokenExpiringSoon', {
        detail: { expiresIn: parseInt(expiresIn) }
      });
      window.dispatchEvent(event);

      // Auto-refresh token if less than 2 minutes remaining
      if (parseInt(expiresIn) < 120 && !isRefreshing) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          refreshAccessToken(refreshToken);
        }
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (token expired or invalid)
    if (error.response && error.response.status === 401) {
      const originalRequestUrl = error.config.url;
      const excludedUrls = ['/auth/login/', '/auth/token/refresh/'];

      // Don't try to refresh on login or refresh endpoints
      if (excludedUrls.some(url => originalRequestUrl.endsWith(url))) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('winguport_user');
        // window.location.href = '/signin';
        window.location.href = `/signin?sessionExpired=true&returnTo=${encodeURIComponent(window.location.pathname)}`
        return Promise.reject(error);
      }

      // Try to refresh the token
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve) => {
            subscribeTokenRefresh((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axios.post(
            `${apiClient.defaults.baseURL}/auth/token/refresh/`,
            { refresh: refreshToken }
          );

          const newAccessToken = response.data.access;
          const newRefreshToken = response.data.refresh || refreshToken;

          localStorage.setItem('authToken', newAccessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Update authorization header
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Notify all queued requests
          onTokenRefreshed(newAccessToken);
          isRefreshing = false;

          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout user
          isRefreshing = false;
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('winguport_user');
          // window.location.href = '/signin';
          window.location.href = `/signin?sessionExpired=true&returnTo=${encodeURIComponent(window.location.pathname)}`
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available, logout
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('winguport_user');
        // window.location.href = '/signin';
        window.location.href = `/signin?sessionExpired=true&returnTo=${encodeURIComponent(window.location.pathname)}`
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to refresh access token
const refreshAccessToken = async (refreshToken) => {
  if (isRefreshing) return;

  isRefreshing = true;
  try {
    const response = await axios.post(
      `${apiClient.defaults.baseURL}/auth/token/refresh/`,
      { refresh: refreshToken }
    );

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh || refreshToken;

    localStorage.setItem('authToken', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

    onTokenRefreshed(newAccessToken);
  } catch (error) {
    console.error('Failed to refresh token:', error);
  } finally {
    isRefreshing = false;
  }
};

export default apiClient;
